
import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { loadLanguage } from '@/i18n';
import { toast } from '@/components/ui/use-toast';

// Available languages - now just English
export type Language = 'en';

// Language context type
type LanguageContextType = {
  language: Language;
  translations: Record<string, string>;
  setLanguage: (lang: Language) => Promise<void>;
  t: (key: string, options?: { [key: string]: any }) => string;
  isLoading: boolean;
  reloadTranslations: () => Promise<void>;
  isInitialized: boolean;
};

// Create the context with default values
const LanguageContext = createContext<LanguageContextType>({
  language: 'en',
  translations: {},
  setLanguage: async () => {},
  t: (key) => key,
  isLoading: true,
  reloadTranslations: async () => {},
  isInitialized: false,
});

// Custom hook for accessing the language context
export const useLanguage = () => useContext(LanguageContext);

// Provider component
interface LanguageProviderProps {
  children: ReactNode;
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
  const [language, setLanguageState] = useState<Language>('en');
  const [translations, setTranslations] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [isInitialized, setIsInitialized] = useState(false);
  
  // Translation helper function with fallback
  const t = useCallback((key: string, options?: { [key: string]: any }): string => {
    let translated = translations[key] || key;
    
    // Simple interpolation for variables if options are provided
    if (options) {
      Object.keys(options).forEach(optionKey => {
        translated = translated.replace(new RegExp(`\\{${optionKey}\\}`, 'g'), String(options[optionKey]));
      });
    }
    
    // If we're in development, warn about missing translations
    if (import.meta.env.DEV && translated === key) {
      console.warn(`Missing translation for key: ${key} in ${language}`);
    }
    
    return translated;
  }, [translations, language]);

  // Function to load translations
  const loadTranslations = useCallback(async (lang: Language): Promise<void> => {
    setIsLoading(true);
    try {
      console.log(`Loading translations for ${lang}`);
      const loadedTranslations = await loadLanguage(lang);
      
      // Validate critical keys
      const criticalKeys = [
        'nav.dashboard',
        'nav.predictions', 
        'nav.titanEngine',
        'nav.tickets',
        'nav.monitoring',
        'nav.profile',
        'nav.settings',
        'nav.help',
        'engine.title',
        'engine.heroTitle',
        'engine.features',
        'engine.selectLeague',
        'engine.selectLeagueTitle',
        'engine.selectDescription',
        'engine.searchLeague',
        'engine.league',
        'engine.type',
        'engine.currentRound',
        'engine.season',
        'app.title',
        'app.language',
        'tickets.title',
        'tickets.create',
        'tickets.list'
      ];
      
      const missingCriticalKeys = criticalKeys.filter(key => !loadedTranslations[key]);
      if (missingCriticalKeys.length > 0) {
        console.warn(`Missing critical keys: ${missingCriticalKeys.join(', ')}`);
      }
      
      setTranslations(loadedTranslations);
      
      // Store the language choice in localStorage
      localStorage.setItem('language', lang);
      
      // Update HTML lang attribute for accessibility
      document.documentElement.setAttribute('lang', lang);
      
      console.log(`Language set to: ${lang}, loaded ${Object.keys(loadedTranslations).length} translation keys`);
    } catch (error) {
      console.error(`Error loading translations for ${lang}:`, error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Function to set the language
  const setLanguage = useCallback(async (lang: Language): Promise<void> => {
    try {
      await loadTranslations(lang);
      setLanguageState(lang);
    } catch (error) {
      console.error(`Failed to set language to ${lang}:`, error);
      // Only show toast if initialized, to prevent error on startup
      if (isInitialized) {
        toast({
          title: 'Error',
          description: `Failed to load language data for ${lang}. Please try again.`,
          variant: 'destructive',
        });
      }
      throw error;
    }
  }, [loadTranslations, isInitialized]);

  // Function to reload translations
  const reloadTranslations = useCallback(async (): Promise<void> => {
    try {
      await loadTranslations(language);
      console.log(`Reloaded translations for ${language}`);
    } catch (error) {
      console.error(`Failed to reload translations for ${language}:`, error);
      if (isInitialized) {
        toast({
          title: 'Error',
          description: `Failed to reload translations. Please try again.`,
          variant: 'destructive',
        });
      }
      throw error;
    }
  }, [language, loadTranslations, isInitialized]);

  // Enhanced route change listener
  useEffect(() => {
    const handleRouteChange = () => {
      console.log('Route changed, checking translations...');
      
      // Check if critical translations are missing
      const criticalKeys = [
        'nav.dashboard', 
        'nav.titanEngine', 
        'nav.tickets',
        'engine.title',
        'app.title',
        'tickets.title'
      ];
      const missingKeys = criticalKeys.filter(key => !translations[key] || translations[key] === key);
      
      if (missingKeys.length > 2 && isInitialized && !isLoading) {
        console.log(`Critical translations missing after route change (${missingKeys.length}), reloading...`);
        reloadTranslations().catch(err => {
          console.error('Failed to reload translations after route change:', err);
        });
      }
    };

    // Listen to popstate events (back/forward navigation)
    window.addEventListener('popstate', handleRouteChange);
    
    // Also listen for custom navigation events
    window.addEventListener('locationchange', handleRouteChange);
    
    return () => {
      window.removeEventListener('popstate', handleRouteChange);
      window.removeEventListener('locationchange', handleRouteChange);
    };
  }, [translations, isInitialized, isLoading, reloadTranslations]);

  // Initialize the language on component mount
  useEffect(() => {
    const initializeLanguage = async () => {
      setIsLoading(true);
      try {
        // Only English is now supported, so we always use 'en'
        let initialLanguage: Language = 'en';
        
        console.log(`Initializing with language: ${initialLanguage}`);
        
        // Set the language and load translations
        setLanguageState(initialLanguage);
        await loadTranslations(initialLanguage);
        
        setIsInitialized(true);
        console.log(`Language initialization complete: ${initialLanguage}`);
      } catch (error) {
        console.error('Failed to load initial translations:', error);
        setIsInitialized(true);
      }
    };
    
    initializeLanguage();
  }, [loadTranslations]); // Only run this once

  return (
    <LanguageContext.Provider
      value={{
        language,
        translations,
        setLanguage,
        t,
        isLoading,
        reloadTranslations,
        isInitialized
      }}
    >
      {children}
    </LanguageContext.Provider>
  );
};
